<template>
  <div class="referral-detail">
    <div class="referral-detail-header">
      <i class="icon icon--document-large"></i>
      <div class="user-info">
        <p class="user-info__company">{{ referralData.deleted ? '-' : referralData.userName }}</p>
        <p class="user-info__userId">{{ referralData.deleted ? $t('referral.referrals.history.withdrawn') : referralData.userId }}</p>
      </div>
      <button class="close-btn" @click="$emit('close')">
        <i class="icon icon--close-btn size-16"></i>
      </button>
    </div>
    <div class="referral-detail-body">
      <div class="referral-detail-body__top">
        <div class="reward">
          <p class="reward__title">{{ $t('referral.referrals.history.totalReward') }}</p>
          <p class="reward__amount">{{ totalReward | thousand }}<span class="reward__unit">JAM</span></p>
          <p class="reward__value">{{ $t('referral.referrals.reward.worth', { worth: getWorth(totalReward).toLocaleString() }) }}</p>
        </div>
        <div class="memo">
          <p class="memo__title">{{ $t('referral.referrals.history.memo') }}</p>
          <div class="memo__wrap">
            <div class="memo__textarea">
              <textarea v-model="memo" maxlength="100" :placeholder="$t('referral.referrals.history.memoPlaceholder')"></textarea>
              <span class="text-count">({{ memo.length }}/{{ maxMemoLength }})</span>
              <ui-toast v-for="t in toasts" :key="t.id" v-bind="t" @close="removeToast"/>
            </div>
            <button class="memo__btn" @click="saveMemo">{{ $t('common.save') }}</button>
          </div>
        </div>
      </div>
      <div class="table--wrap" :class="{'has-scroll': rewardList.length > 6}">
        <div class="table--wrap__top">
          <p class="table-title">{{ $t('referral.referrals.history.referralRewardsHistory') }}</p>
          <div class="table-tools">
            <app-pagination
              class="clearfix"
              :total="count"
              :page="page"
              :limit="limit"
              :pagingBlock="5"
              @onPagingOptionChange="onPagingOptionChange" />
          </div>
        </div>
        <div class="table--wrap__inner" ref="scrollArea">
          <data-table :headers="headers" :sortColumn="sort" @changeSort="changeSort">
            <template v-slot:colgroup>
              <colgroup>
                <col width="11.4%">
                <col width="11.8%">
                <col width="11.8%">
                <col width="9.8%">
                <col width="9.8%">
                <col width="12%">
                <col width="13.4%">
                <col width="9.8%">
                <col width="9.8%">
              </colgroup>
            </template>
            <tbody>
              <tr v-for="(item, index) in rewardList" :key="index">
                <td class="data-referDate">{{ item.referDate | dashDate }}</td>
                <td class="data-service">{{ item.serviceType === 'ba' ? 'Journey Map' : 'UX Heatmap' }}</td>
                <td class="data-planName">{{ item.planName }}</td>
                <td class="data-useStartDate">{{ item.useStartDate | dashDate }}</td>
                <td class="data-useEndDate">{{ item.useEndDate | dashDate }}</td>
                <td class="data-amount">₩ {{ item.amount | thousand }}</td>
                <td class="data-reward">{{ item.reward | thousand }} JAM</td>
                <td v-if="item.rewardDate" class="data-rewardDate">{{ item.rewardDate | dashDate }}</td>
                <td v-else class="data-rewardDate awaiting">{{ $t(`referral.referrals.history.awaiting`) }}</td>
                <td :class="['data-rewardState', item.rewardState]">{{ $t(`referral.referrals.history.${item.rewardState}`) }}</td>
              </tr>
            </tbody>
          </data-table>
          <div class="no-data" v-if="!rewardList.length">
            <i class="icon icon--empty-bg"></i>
            <p v-html="$t('referral.referrals.history.noRewardDetail')"></p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getReferralHistory, saveMemo } from '@/api/referral'

import DataTable from '@/components/ui/DataTable'
import UiToast from '@/components/ui/UiToast'
import AppPagination from '@/components/ui/AppPagination'

export default {
  name: 'ReferralDetail',
  components: {
    DataTable,
    AppPagination,
    UiToast
  },
  props: {
    referralData: {
      type: Object,
      default: () => {}
    },
    referralCode: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      page: 1,
      limit: 30,
      sort: '-referDate',
      hasScroll: false,
      maxMemoLength: 100,
      memo: '',
      totalReward: 0,
      rewardList: [],
      count: 0,
      toasts: [],
      toastId: 0
    }
  },
  computed: {
    ...mapState([
      'userData'
    ]),
    headers () {
      return [
        {
          name: this.$t('referral.referrals.history.referralDate'),
          sortable: true,
          value: 'referDate'
        },
        {
          name: `${this.$t('referral.referrals.history.service')}`,
          sortable: true,
          value: 'serviceType'
        },
        {
          name: this.$t('referral.referrals.history.planName'),
          sortable: true,
          value: 'planName'
        },
        {
          name: this.$t('referral.referrals.history.startDate'),
          sortable: true,
          value: 'useStartDate'
        },
        {
          name: this.$t('referral.referrals.history.endDate'),
          sortable: true,
          value: 'useEndDate'
        },
        {
          name: this.$t('referral.referrals.history.payment'),
          sortable: true,
          value: 'amount'
        },
        {
          name: this.$t('referral.referrals.history.reward'),
          sortable: true,
          value: 'reward',
          tooltip: this.$t('referral.referrals.history.paymentTooltip')
        },
        {
          name: this.$t('referral.referrals.history.rewardDate'),
          sortable: true,
          value: 'rewardDate'
        },
        {
          name: this.$t('referral.referrals.history.status'),
          sortable: true,
          value: 'rewardState'
        }
      ]
    }
  },
  created () {
    this.loadData()
  },
  beforeDestroy () {
  },
  methods: {
    getWorth (count) {
      if (this.$i18n.locale === 'en') {
        return count
      } else if (this.$i18n.locale === 'ja') {
        return count * 100
      } else {
        return count * 1000
      }
    },
    loadData () {
      this.isLoading = true

      const payload = {
        page: this.page - 1,
        limit: this.limit,
        order: this.sort,
        userId: this.referralData.userId,
        referral: this.referralCode
      }

      getReferralHistory(payload).then(res => {
        this.rewardList = res.list
        this.totalReward = res.rewardTotal
        this.memo = res.memo
        this.count = res.count
      }).catch(e => {
        console.log(e)
      }).finally(() => {
        this.isLoading = false
      })
    },
    saveMemo () {
      saveMemo({
        userId: this.referralData.userId,
        referral: this.referralCode,
        memo: this.memo
      }).then(res => {
        this.showToast(this.$t('referral.referrals.history.saved'))
      }).catch(e => {
        this.showToast(this.$t('referral.referrals.history.failed'))
      })
    },
    showToast (message) {
      this.toasts.push({ id: this.toastId++, message })
    },
    removeToast (id) {
      this.toasts = this.toasts.filter(toast => toast.id !== id)
    },
    onPagingOptionChange (page, limit) {
      this.page = page
      this.limit = limit
    },
    changeSort (sort) {
      this.sort = sort
      this.page = 1
      this.loadData()
    }
  }
}
</script>

<style lang="scss" scoped>
.referral-detail {
  display: flex;
  flex-direction: column;
  height: 749px;
  width: 1080px;
  padding: 0 28px;
  border-radius: 14px;
  background-color: #fff;
  font-weight: 400;
  color: #2f2f2f;
  box-sizing: border-box;
  &-header {
    @include flexYcenter;
    gap: 25px;
    padding: 30px 0 24px;

    .user-info {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 8px;

      &__company {
        color: #2F2F2F;
        font-size: 22px;
        font-weight: 600;
      }
      &__userId {
        color: #777;
        font-size: 12px;
        line-height: 150%;
      }
      &::before {
        position: absolute;
        content: '';
        display: block;
        width: 1px;
        height: 100%;
        background-color: #D7D7D7;
        left: -12px;
      }
    }
    .close-btn {
      display: flex;
      position: absolute;
      padding: 12px;
      top: 0;
      right: 0;
    }
  }
  &-body {
    padding-bottom: 24px;
    &__top {
      display: flex;
      gap: 7px;
      padding: 20px 4px 24px 8px;
      border-top: 1px dashed #D7D7D7;
      border-bottom: 1px dashed #D7D7D7;

      .reward {
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 400px;
        padding-top: 11px;

        &__title {
          color: #666;
          font-size: 13px;
          font-weight: 600;
        }
        &__amount {
          color: #F46200;
          font-size: 36px;
          font-weight: 600;
        }
        &__value {
          margin-left: 2px;
          color: #888;
          font-size: 13px;
        }
        &__unit {
          color: #F46200;
          font-size: 16px;
          font-weight: 600;
          padding: 0 0 4px 8px;
        }
      }
      .memo {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        &__title {
          margin-left: 4px;
          color: #777;
          font-size: 13px;
          font-weight: 600;
          line-height: 120%;
        }
        &__wrap {
          display: flex;
          gap: 8px;
          .memo__textarea {
            position: relative;
            width: 100%;
            height: 82px;
            padding: 12px 12px 32px;
            border-radius: 5px;
            border: 1px solid #BBB;
            background-color: #FFF;
            box-sizing: border-box;
            textarea {
              width: 100%;
              height: 100%;
              border: none;
              background-color: transparent;
              resize: none;
              color: #2F2F2F;
              font-size: 14px;
              line-height: 150%;
            }
            .text-count {
              position: absolute;
              bottom: 12px;
              right: 12px;
              color: #999;
              font-size: 11px;
            }
            .memo-alert {
              display: none;
              align-items: center;
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              height: 32px;
              padding: 6px 16px;
              border-radius: 6px;
              background-color: #000;
              box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.16);
              color: #68BB0C;
              font-size: 13px;
              font-weight: 400;
              line-height: 120%;
              white-space: nowrap;
            }
          }
          .memo__btn {
            height: 28px;
            padding: 0px 12px;
            border-radius: 4px;
            background: #EC0047;
            color: #fff;
            font-size: 12px;
            font-weight: 600;
            line-height: 100%;
            white-space: nowrap;
          }
        }
      }
    }
    .table--wrap {
      position: relative;
      &.has-scroll {
        &::before {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 12px;
          background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.06) 100%);
        }
      }
      &__top {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px 4px;
        .table-title {
          flex: 1;
          color: #2F2F2F;
          font-size: 15px;
          font-weight: 600;
        }
        .table-tools {
          @include flexYcenter;
          gap: 32px;
          .page__comm--wrap {
            margin-bottom: 0;
          }
        }
      }
      &__inner {
        height: 353px;
        overflow-x: hidden;
        overflow-y: auto;
        padding: 0 5px;
        margin-top: 50px;
        /deep/ table {
          thead {
            position: fixed;
            width: calc(100% - 66px);
            display: table;
            transform: translateY(-50px);
            tr {
              background-color: #333;
              th {
                display: inline-flex;
                align-items: flex-end;
                padding: 6px 12px 8px 12px;
                text-align: start;
                vertical-align: bottom;
                box-sizing: border-box;
                span {
                  line-height: 1.2;
                }
                .sub {
                  color: #999;
                }
                &:first-of-type:before,
                &:last-of-type:before {
                  background-color: #333;
                }
                &.data-referDate {
                  width: 116px;
                  justify-content: center;
                }
                &.data-serviceType,
                &.data-planName {
                  width: 120px;
                }
                &.data-amount {
                  width: 122px;
                }
                &.data-useStartDate,
                &.data-useEndDate,
                &.data-rewardDate,
                &.data-rewardState {
                  width: 100px;
                  justify-content: center;
                }
                &.data-reward {
                  width: 136px;
                  justify-content: end;
                }
              }
            }
          }
          tbody {
            tr {
              border-top: 1px solid #ebebeb;
              text-align: start;

              &:first-child {
                border-top: none
              }

              &:last-child {
                border-bottom: 1px solid #ebebeb;
              }

              td {
                height: 60px;
                padding: 12px;
                font-size: 13px;
                line-height: 1.2;
                color: #444;
                position: relative;
                box-sizing: border-box;

                &.data-referDate,
                &.data-useStartDate,
                &.data-useEndDate,
                &.data-rewardDate {
                  text-align: center;
                  &.awaiting {
                    color: #888;
                  }
                }
                &.data-reward {
                  text-align: end;
                }
                &.data-rewardState {
                  text-align: center;
                  font-weight: 600;
                  &.pending {
                    color: #9665E3;
                  }
                  &.confirmed {
                    color: #57AB00;
                  }
                }
              }
            }
          }
        }
        .no-data {
          @include flexXYcenter;
          flex-direction: column;
          gap: 8px;
          height: 100%;
          padding-bottom: 32px;
          box-sizing: border-box;

          color: #666;
          font-size: 14px;
          font-weight: 400;
          line-height: 1.6;
          text-align: center;
        }
      }
    }
  }
}
</style>
